import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../states/containers/store'
import { format, getYear, nextMonth, nowDate, prevMonth, startOfMonth } from '../../../utils/date'
import { getGetPrams } from '../../../utils/url'

interface Props {
  date: string
  changeCallback: (date: string) => void
  startMonth?: string
}

interface GetParams {
  date: string
}

const DateModule: FC<Props> = (props) => {
  const { t } = useTranslation()
  const location = useLocation()

  const {
    commonPart,
    analyze,
  } = useSelector((state: RootState) => state)

  const {
    date,
    changeCallback,
    startMonth,
  } = props

  const [startDate, setStartDate] = useState<string>()
  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)
  const [isClickable, setIsClickable] = useState(true)
  const [targetYear, setTargetYear] = useState<number>(Number(`${format(prevMonth(nowDate()), "YYYY")}`))

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const startMonthNum = useMemo(() => {
    if (startMonth) {
      return Number(startMonth)
    }
    return Number(format(commonPart.company?.created_at, 'YYYYMM'))
  }, [startMonth])

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    setStartDate(format(value))
    changeCallback(value)
  }

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (!getParams.date) {
      setStartDate(date)
    } else {
      setStartDate(format(getParams.date))
    }
  }, [location, date])

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isCalendarDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isCalendarDisplay])

  useEffect(() => {
    setIsClickable(!analyze.isLoading)
  }, [analyze.isLoading])

  return (
    <>
      <div className="calendar_text">
        <button
          type="button"
          className="display button"
          onClick={() => setIsCalendarDisplay(true)}
          disabled={!isClickable}
        >
          {format(startDate, "YYYY年MM月")}
          <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
        </button>
        <button
          type="button"
          className="prev button"
          onClick={() => handleChangeMonth(prevMonth(startDate || nowDate(), 'YYYY-MM-01'))}
          disabled={
            !commonPart.company?.created_at
            || Number(prevMonth(startDate || nowDate(), 'YYYYMM')) < startMonthNum
            || !isClickable
          }
        >
          <FontAwesomeIcon icon={['fas', 'angle-left']} />
        </button>
        <button
          type="button"
          className="next button"
          onClick={() => handleChangeMonth(nextMonth(startDate || nowDate(), 'YYYY-MM-01'))}
          disabled={
            !commonPart.company?.created_at
            || Number(nextMonth(startDate || nowDate(), 'YYYYMMDD')) >= Number(nowDate('YYYYMMDD'))
            || !isClickable
          }
        >
          <FontAwesomeIcon icon={['fas', 'angle-right']} />
        </button>
      </div>
      <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
        <div className="range">
          <div className="text">
            {t(`range.calendar.header_text.monthly`)}
          </div>
        </div>
        <div className="monthly">
          <div className="select_month">
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear - 1)}
                disabled={Number(format(`${startMonthNum}01`, 'YYYY')) >= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
            </div>
            <div>
              {`${targetYear}${t('time.time.short.year')}`}
            </div>
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear + 1)}
                disabled={getYear() <= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </div>
          </div>
          {
            month.map((v, i) => {
              const targetYearDate = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
              const isClickableMonth = Number(nowDate('YYYYMM')) >= Number(targetYearDate)
                && startMonthNum <= Number(targetYearDate)
                && Number(`${targetYearDate}01`) !== Number(nowDate('YYYYMMDD'))
              const isSelected = format(startDate, 'YYYYMM') === targetYearDate
              return (
                <div className="monthly_button" key={v}>
                  <button
                    type="button"
                    className={isSelected ? "selected" : ""}
                    onClick={() => handleChangeMonth(`${String(targetYear)}-${(`0${String(i + 1)}`).slice(-2)}-01`)}
                    disabled={!isClickableMonth}
                  >
                    {`${v}${t('time.time.short.month')}`}
                  </button>
                </div>
              )
            })
          }
        </div>
      </div>
      <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
    </>
  )
}

DateModule.defaultProps = {
  startMonth: "",
}

export default DateModule
